import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { NOMINATIM_BASE } from '../../utils/nominatim';
import { formatUserDateTime } from '../../utils/dateFormat';
import { deviceDisplayName } from '../../utils/deviceName';
import { Copy, Mail, Phone, Check, Loader2, Link2, Clock, MapPin, Navigation, Route, Search, AlertTriangle, CheckCircle, Globe, MapIcon } from 'lucide-react';
import { AddressAutocomplete } from '../AddressAutocomplete';

const DURATION_OPTIONS = [
  { value: '10', label: '10 minutes' },
  { value: '30', label: '30 minutes' },
  { value: '60', label: '1 hour' },
  { value: '120', label: '2 hours' },
  { value: '240', label: '4 hours' },
  { value: '480', label: '8 hours' },
  { value: '720', label: '12 hours' },
  { value: '1440', label: '24 hours' },
  { value: 'custom', label: 'Custom date/time' },
];

export const CreateTrackingLinkModal = ({ open, onOpenChange, device, api, onCreated, dateFormat = 'DD/MM/YYYY', timeFormat = '24h', userTimezone }) => {
  const { isFeatureEnabled, isNotificationChannelActive, hasGoogleMaps } = useFeatureFlags();
  const [creating, setCreating] = useState(false);
  const [createdLink, setCreatedLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [modalAlert, setModalAlert] = useState(null); // {success: bool, message: string}

  // Config state
  const [expiryType, setExpiryType] = useState('time');
  const [duration, setDuration] = useState('60');
  const [customExpiry, setCustomExpiry] = useState('');
  const [geofenceCoords, setGeofenceCoords] = useState('');
  const [geofenceRadius, setGeofenceRadius] = useState('100');
  const [geofenceAddress, setGeofenceAddress] = useState('');
  const [addressSearching, setAddressSearching] = useState(false);
  const [addressResults, setAddressResults] = useState([]);
  const [showTelemetry, setShowTelemetry] = useState(false);
  const [showCommands, setShowCommands] = useState(false);
  const [showEvents, setShowEvents] = useState(false);
  const [showEta, setShowEta] = useState(true);
  const [showMapOption, setShowMapOption] = useState(true);
  const [mapProvider, setMapProvider] = useState('osm');
  const [useDirectionsApi, setUseDirectionsApi] = useState(false);
  const [useOsrmRoute, setUseOsrmRoute] = useState(false);

  // Share state
  const [shareTab, setShareTab] = useState('link');
  const [shareRecipient, setShareRecipient] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [sending, setSending] = useState(false);

  // Reset all state when modal opens
  useEffect(() => {
    if (open) {
      setCreating(false);
      setCreatedLink(null);
      setCopied(false);
      setExpiryType('time');
      setDuration('60');
      setCustomExpiry('');
      setGeofenceCoords('');
      setGeofenceRadius('100');
      setGeofenceAddress('');
      setAddressResults([]);
      setShowTelemetry(false);
      setShowCommands(false);
      setShowEvents(false);
      setShowEta(true);
      setShowMapOption(true);
      setMapProvider('osm');
      setUseDirectionsApi(false);
      setShareTab('link');
      setShareRecipient('');
      setShareMessage('');
      setModalAlert(null);
    }
  }, [open]);

  const canEmail = isNotificationChannelActive('notification_email');
  const canSMS = isNotificationChannelActive('notification_sms');
  const canTelemetry = isFeatureEnabled('live_tracking_telemetry_data');
  const canCommands = isFeatureEnabled('live_tracking_commands');
  const canEvents = isFeatureEnabled('live_tracking_events');
  const canDirectionsEta = isFeatureEnabled('google_directions_eta');
  const canOsrmRouteEta = isFeatureEnabled('osrm_route_eta');

  const deviceName = deviceDisplayName(device);

  const handleSearchAddress = async () => {
    if (!geofenceAddress.trim()) return;
    setAddressSearching(true);
    setAddressResults([]);
    try {
      // Use Google Geocoder (bundled with Maps JS API, more accurate with house numbers)
      if (window.google?.maps?.Geocoder) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: geofenceAddress }, (results, status) => {
          setAddressSearching(false);
          if (status === 'OK' && results.length > 0) {
            setAddressResults(results.map(r => ({
              display_name: r.formatted_address,
              lat: r.geometry.location.lat().toString(),
              lon: r.geometry.location.lng().toString(),
            })));
          } else {
            // Fallback to Nominatim
            fetchNominatim();
          }
        });
        return;
      }
      // Fallback: Nominatim
      await fetchNominatim();
    } catch { toast.error('Address search failed'); setAddressSearching(false); }

    async function fetchNominatim() {
      try {
        const res = await fetch(`${NOMINATIM_BASE}/search?format=json&q=${encodeURIComponent(geofenceAddress)}&limit=5`);
        const results = await res.json();
        if (results.length > 0) {
          setAddressResults(results.map(r => ({ display_name: r.display_name, lat: r.lat, lon: r.lon })));
        } else {
          toast.error('No results found');
        }
      } catch { toast.error('Address search failed'); }
      finally { setAddressSearching(false); }
    }
  };

  const handleSelectAddress = (result) => {
    setGeofenceCoords(`${result.lat}, ${result.lon}`);
    setGeofenceAddress(result.display_name);
    setAddressResults([]);
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const payload = {
        device_id: device.id,
        expiry_type: expiryType,
        show_telemetry: showTelemetry,
        show_commands: showCommands,
        show_events: showEvents,
        show_map: showMapOption,
        show_eta: showEta,
        use_directions_api: useDirectionsApi,
        use_osrm_route: useOsrmRoute,
        map_provider: showMapOption ? mapProvider : 'osm',
      };

      if (expiryType === 'time' || expiryType === 'both') {
        if (duration === 'custom') {
          if (!customExpiry) { toast.error('Please set a custom expiry date/time'); setCreating(false); return; }
          payload.custom_expiry = new Date(customExpiry).toISOString();
        } else {
          payload.duration = parseInt(duration);
        }
      }

      if (expiryType === 'geofence' || expiryType === 'both') {
        const parts = geofenceCoords.split(',').map(s => parseFloat(s.trim()));
        if (parts.length !== 2 || isNaN(parts[0]) || isNaN(parts[1])) { toast.error('Please enter valid coordinates (e.g. -33.910661, 151.190214)'); setCreating(false); return; }
        payload.geofence_lat = parts[0];
        payload.geofence_lng = parts[1];
        payload.geofence_radius = parseInt(geofenceRadius) || 100;
        payload.geofence_address = geofenceAddress;
      }

      const res = await api.post('/tracking-links', payload);
      setCreatedLink(res.data);
      toast.success('Tracking link created');
      onCreated?.();
    } catch (err) {
      setModalAlert({ success: false, message: err.response?.data?.detail || 'Failed to create tracking link' });
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    if (!createdLink?.url) return;
    try {
      await navigator.clipboard.writeText(createdLink.url);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Failed to copy'); }
  };

  const handleShare = async () => {
    if (!shareRecipient.trim()) {
      toast.error(shareTab === 'email' ? 'Enter an email address' : 'Enter a phone number');
      return;
    }
    setSending(true);
    try {
      await api.post('/share/tracking-link', {
        tracking_url: createdLink.url,
        device_name: deviceName,
        method: shareTab,
        recipient: shareRecipient.trim(),
        message: shareMessage.trim(),
        expires_at: createdLink.expires_at ? formatUserDateTime(createdLink.expires_at, dateFormat, timeFormat, userTimezone) : '',
      });
      setModalAlert({ success: true, message: `Link shared via ${shareTab === 'email' ? 'email' : 'SMS'}` });
      setShareRecipient('');
      setShareMessage('');
    } catch (err) {
      setModalAlert({ success: false, message: err.response?.data?.detail || 'Failed to share' });
    } finally {
      setSending(false);
    }
  };

  const handleClose = (v) => {
    if (!v) {
      if (createdLink) onCreated?.(); // Refresh badge when closing after link creation
      setCreatedLink(null); setShareRecipient(''); setShareMessage('');
    }
    onOpenChange(v);
  };

  if (!device) return null;

  // After link is created — show share options
  if (createdLink) {
    const shareTabs = [{ id: 'link', label: 'Copy Link', icon: Copy }];
    if (canEmail) shareTabs.push({ id: 'email', label: 'Email', icon: Mail });
    if (canSMS) shareTabs.push({ id: 'sms', label: 'SMS', icon: Phone });

    return (
      <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-md" data-testid="tracking-link-share-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-[#2563eb]" />
              Tracking Link Created
            </DialogTitle>
          </DialogHeader>

          <div className="p-3 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 text-sm">
            <p className="font-semibold text-green-800 dark:text-green-300">{deviceName}</p>
            {createdLink.expires_at && (
              <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                Expires: {formatUserDateTime(createdLink.expires_at, dateFormat, timeFormat, userTimezone)}
              </p>
            )}
          </div>

          <div className="flex gap-1 border-b">
            {shareTabs.map(t => (
              <button key={t.id} onClick={() => setShareTab(t.id)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${shareTab === t.id ? 'border-b-2 border-[#2563eb] text-[#2563eb]' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid={`tracking-share-tab-${t.id}`}
              >
                <t.icon className="w-3.5 h-3.5" />{t.label}
              </button>
            ))}
          </div>

          {shareTab === 'link' && (
            <div className="flex gap-2">
              <Input value={createdLink.url} readOnly className="text-xs bg-muted" data-testid="tracking-link-url" />
              <Button onClick={handleCopy} className="shrink-0" data-testid="tracking-link-copy">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          )}

          {shareTab === 'email' && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Recipient Email</Label>
                <Input type="email" value={shareRecipient} onChange={e => setShareRecipient(e.target.value)} placeholder="email@example.com" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Message (optional)</Label>
                <Textarea value={shareMessage} onChange={e => setShareMessage(e.target.value)} placeholder="Add a note..." rows={2} className="text-sm" />
              </div>
              <Button onClick={handleShare} disabled={sending} className="w-full">
                {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                Send Email
              </Button>
            </div>
          )}

          {shareTab === 'sms' && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Recipient Phone Number</Label>
                <Input type="tel" value={shareRecipient} onChange={e => setShareRecipient(e.target.value)} placeholder="+61 400 000 000" />
              </div>
              <Button onClick={handleShare} disabled={sending} className="w-full">
                {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Phone className="w-4 h-4 mr-2" />}
                Send SMS
              </Button>
            </div>
          )}
          {modalAlert && (
            <div className={`p-3 rounded-lg border text-center ${modalAlert.success ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800'}`}>
              <div className="flex items-center justify-center gap-2">
                {modalAlert.success ? <CheckCircle className="w-4 h-4 text-green-500" /> : <AlertTriangle className="w-4 h-4 text-red-500" />}
                <p className="text-sm font-medium">{modalAlert.message}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </>
    );
  }

  // Configuration form
  return (
    <>
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="create-tracking-link-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-[#2563eb]" />
            Generate Tracking Link
          </DialogTitle>
        </DialogHeader>

        <div className="p-3 rounded-lg bg-muted/50 border text-sm">
          <p className="font-semibold">{deviceName}</p>
          <p className="text-xs text-muted-foreground">Create a public, no-login-required tracking link</p>
        </div>

        <div className="space-y-4">
          {/* Expiry Type */}
          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-1.5"><Clock className="w-4 h-4" /> Expiry Type</Label>
            <Select value={expiryType} onValueChange={setExpiryType}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="time">Time-based</SelectItem>
                <SelectItem value="geofence">Geofence arrival</SelectItem>
                <SelectItem value="both">Both (whichever comes first)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Time Expiry */}
          {(expiryType === 'time' || expiryType === 'both') && (
            <div className="space-y-2 p-3 rounded-lg border bg-muted/30">
              <Label className="text-xs font-medium">Duration</Label>
              <Select value={duration} onValueChange={setDuration}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DURATION_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
              {duration === 'custom' && (
                <Input type="datetime-local" value={customExpiry} onChange={e => setCustomExpiry(e.target.value)} className="h-9" />
              )}
            </div>
          )}

          {/* Geofence Expiry */}
          {(expiryType === 'geofence' || expiryType === 'both') && (
            <div className="space-y-2 p-3 rounded-lg border bg-muted/30">
              <Label className="text-xs font-medium flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Geofence Target</Label>
              <AddressAutocomplete
                value={geofenceAddress}
                onChange={setGeofenceAddress}
                onSelect={(s) => {
                  setGeofenceCoords(`${s.lat}, ${s.lon}`);
                }}
                placeholder="Search address..."
                inputClassName="h-9 text-sm"
                data-testid="geofence-address"
                limit={5}
              />
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <Label className="text-[10px] text-muted-foreground">Coordinates</Label>
                  <Input value={geofenceCoords} onChange={e => setGeofenceCoords(e.target.value)} placeholder="-33.910661, 151.190214" className="h-8 text-xs font-mono" data-testid="geofence-coords" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Radius (m)</Label>
                  <Input type="number" value={geofenceRadius} onChange={e => setGeofenceRadius(e.target.value)} placeholder="100" className="h-8 text-xs" />
                </div>
              </div>
              <p className="text-[10px] text-muted-foreground">Search an address or paste coordinates (lat, lng). Link expires when device arrives within radius and stops.</p>
            </div>
          )}

          {/* Content Toggles */}
          <div className="space-y-2 p-3 rounded-lg border bg-muted/30">
            <Label className="text-xs font-medium">Content Options</Label>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm">Allow Map</span>
                <Switch checked={showMapOption} onCheckedChange={(v) => { setShowMapOption(v); if (!v) setMapProvider('osm'); }} />
              </div>
              {showMapOption && (
                <div className="flex items-center gap-2 pl-2 border-l-2 border-muted">
                  <button
                    onClick={() => setMapProvider('osm')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                      mapProvider === 'osm'
                        ? 'bg-[#2563eb] text-white'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                    data-testid="map-provider-osm"
                  >
                    <MapIcon className="w-3.5 h-3.5" />
                    OSM
                  </button>
                  {hasGoogleMaps && (
                    <button
                      onClick={() => setMapProvider('google')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                        mapProvider === 'google'
                          ? 'bg-[#2563eb] text-white'
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                      data-testid="map-provider-google"
                    >
                      <Globe className="w-3.5 h-3.5" />
                      Google Maps
                    </button>
                  )}
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm">Show ETA</span>
                <Switch checked={showEta} onCheckedChange={setShowEta} />
              </div>
              {canDirectionsEta && (
                <div className="flex items-center justify-between">
                  <span className="text-sm flex items-center gap-1.5"><Navigation className="w-3.5 h-3.5 text-blue-500" /> Google Directions ETA</span>
                  <Switch checked={useDirectionsApi} onCheckedChange={setUseDirectionsApi} data-testid="tracking-link-directions-eta" />
                </div>
              )}
              {canOsrmRouteEta && (
                <div className="flex items-center justify-between">
                  <span className="text-sm flex items-center gap-1.5"><Route className="w-3.5 h-3.5 text-emerald-500" /> OSRM Route ETA</span>
                  <Switch checked={useOsrmRoute} onCheckedChange={setUseOsrmRoute} data-testid="tracking-link-osrm-route-eta" />
                </div>
              )}
              {canTelemetry && (
                <div className="flex items-center justify-between">
                  <span className="text-sm">Show Telemetry Data</span>
                  <Switch checked={showTelemetry} onCheckedChange={setShowTelemetry} />
                </div>
              )}
              {canCommands && (
                <div className="flex items-center justify-between">
                  <span className="text-sm">Allow Commands</span>
                  <Switch checked={showCommands} onCheckedChange={setShowCommands} />
                </div>
              )}
              {canEvents && (
                <div className="flex items-center justify-between">
                  <span className="text-sm">Show Events</span>
                  <Switch checked={showEvents} onCheckedChange={setShowEvents} />
                </div>
              )}
            </div>
          </div>

          <Button onClick={handleCreate} disabled={creating} className="w-full" data-testid="create-tracking-link-btn">
            {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
            Generate Link
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};
